import { CheckCircle, XCircle, ArrowRight, ChevronDown, ChevronUp, Minus, Plus } from 'lucide-react';
import { useState } from 'react';

// Feature definitions per plan tier
const PLAN_TIERS = {
  // Founder plans (ordered by tier)
  'Founder Free': {
    tier: 1,
    features: ['Profile creation', '5 investor previews/month', '5 match suggestions/month']
  },
  'Founder Pro': {
    tier: 2,
    features: ['Profile creation', 'Unlimited investor previews', 'Unlimited match suggestions', 'Direct messaging', 'Pitch deck upload', 'NDA-gated content', 'Co-founder search']
  },
  'Founder Elite': {
    tier: 3,
    features: ['Profile creation', 'Unlimited investor previews', 'Unlimited match suggestions', 'Direct messaging', 'Pitch deck upload', 'NDA-gated content', 'Co-founder search', 'Featured visibility', 'Curated investor introductions', 'Pitch deck feedback', 'Priority support']
  },
  // Investor plans
  'Investor Free': {
    tier: 1,
    features: ['Profile creation', '15 startup views/month', 'Save to watchlist', '1 pitch preview/month', '1 message initiation/month']
  },
  'Investor Pro': {
    tier: 2,
    features: ['Profile creation', 'Unlimited startup discovery', 'Save to watchlist', 'Unlimited pitch deck access', 'Unlimited messaging', 'NDA pitch deck access', 'Advanced filters']
  },
  'Investor Premium': {
    tier: 3,
    features: ['Profile creation', 'Unlimited startup discovery', 'Save to watchlist', 'Unlimited pitch deck access', 'Unlimited messaging', 'NDA pitch deck access', 'Advanced filters', 'Curated deal flow', '48h early access', 'Dedicated account manager']
  },
  // Co-founder plans
  'Co-Founder Free': {
    tier: 1,
    features: ['Profile creation', 'Browse profiles', '5 matches/month']
  },
  'Co-Founder Plus': {
    tier: 2,
    features: ['Profile creation', 'Browse profiles', 'Unlimited matching', 'Unlimited messaging', 'Visibility boost', 'Advanced filters']
  }
};

export default function PlanComparison({ currentPlan, selectedPlan, billingCycle }) {
  const [expanded, setExpanded] = useState(true);

  if (!selectedPlan || !currentPlan) return null;

  const currentPlanName = currentPlan;
  const newPlanName = selectedPlan.plan_name;
  
  const currentTierData = PLAN_TIERS[currentPlanName] || { tier: 0, features: [] };
  const newTierData = PLAN_TIERS[newPlanName] || { tier: 0, features: [] };
  
  const currentFeatures = currentTierData.features;
  const newFeatures = newTierData.features;

  // Determine if upgrade or downgrade based on tier
  const isUpgrade = newTierData.tier > currentTierData.tier;
  const isDowngrade = newTierData.tier < currentTierData.tier;
  const isSameTier = newTierData.tier === currentTierData.tier;

  // Calculate prices
  const newPrice = billingCycle === 'yearly' 
    ? selectedPlan.yearly_price_usd 
    : selectedPlan.monthly_price_usd;
  
  // Estimate current price
  const currentPriceApprox = currentPlanName.includes('Free') ? 0 
    : currentPlanName.includes('Pro') ? (billingCycle === 'yearly' ? 490 : 49)
    : currentPlanName.includes('Elite') || currentPlanName.includes('Premium') ? (billingCycle === 'yearly' ? 1290 : 129)
    : currentPlanName.includes('Plus') ? (billingCycle === 'yearly' ? 190 : 19) : 0;

  const priceDiff = newPrice - currentPriceApprox;

  // For UPGRADE: Show what stays (inherited) + what's new (added)
  // For DOWNGRADE: Show what's available (new plan features) + what's removed (losing)
  
  let staysFeatures = [];
  let addedFeatures = [];
  let availableFeatures = [];
  let removedFeatures = [];

  if (isUpgrade) {
    // Features that exist in both = STAYS
    staysFeatures = currentFeatures.filter(f => newFeatures.includes(f));
    // Features only in new plan = ADDED
    addedFeatures = newFeatures.filter(f => !currentFeatures.includes(f));
  } else if (isDowngrade) {
    // All features in new plan = AVAILABLE
    availableFeatures = newFeatures;
    // Features in current but not in new = REMOVED
    removedFeatures = currentFeatures.filter(f => !newFeatures.includes(f));
  }

  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden mb-6">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-4 bg-slate-50 hover:bg-slate-100 transition-colors"
      >
        <div className="flex items-center gap-2">
          <span className={`text-sm font-medium ${
            isUpgrade ? 'text-green-700' : isDowngrade ? 'text-amber-700' : 'text-blue-700'
          }`}>
            {isUpgrade ? '📈 Upgrade Summary' : isDowngrade ? '📉 Downgrade Summary' : '🔄 Plan Change'}
          </span>
        </div>
        {expanded ? <ChevronUp size={18} className="text-blue-600" /> : <ChevronDown size={18} className="text-blue-600" />}
      </button>

      {expanded && (
        <div className="p-4 space-y-4">
          {/* Price Change */}
          <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-100">
            <div>
              <span className="text-blue-800 font-medium">Price Change</span>
              <p className="text-blue-600 text-xs mt-0.5">
                {currentPlanName} → {newPlanName}
              </p>
            </div>
            <div className="text-right">
              <span className={`font-bold text-lg ${priceDiff > 0 ? 'text-amber-600' : priceDiff < 0 ? 'text-green-600' : 'text-blue-600'}`}>
                {priceDiff === 0 ? 'Same price' : priceDiff > 0 ? `+$${priceDiff}` : `-$${Math.abs(priceDiff)}`}
              </span>
              <span className="text-blue-600 text-sm">/{billingCycle === 'yearly' ? 'yr' : 'mo'}</span>
            </div>
          </div>

          {/* UPGRADE VIEW */}
          {isUpgrade && (
            <>
              {/* What Stays */}
              {staysFeatures.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-blue-700 mb-2 flex items-center gap-2">
                    <Minus size={16} />
                    What You Keep
                  </h4>
                  <ul className="space-y-1">
                    {staysFeatures.map((feature, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-blue-700 bg-slate-50 p-2 rounded border border-slate-100">
                        <CheckCircle size={14} className="flex-shrink-0 mt-0.5 text-blue-500" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* What's Added */}
              {addedFeatures.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-green-700 mb-2 flex items-center gap-2">
                    <Plus size={16} />
                    What You'll Get
                    <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">+{addedFeatures.length} new</span>
                  </h4>
                  <ul className="space-y-1.5">
                    {addedFeatures.map((feature, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-green-800 bg-green-50 p-2 rounded border border-green-100">
                        <CheckCircle size={14} className="flex-shrink-0 mt-0.5 text-green-600" />
                        <span className="font-medium">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}

          {/* DOWNGRADE VIEW */}
          {isDowngrade && (
            <>
              {/* What's Available */}
              {availableFeatures.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-blue-700 mb-2 flex items-center gap-2">
                    <CheckCircle size={16} />
                    What's Included in {newPlanName}
                  </h4>
                  <ul className="space-y-1">
                    {availableFeatures.map((feature, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-blue-700 bg-slate-50 p-2 rounded border border-slate-100">
                        <CheckCircle size={14} className="flex-shrink-0 mt-0.5 text-blue-500" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* What's Removed */}
              {removedFeatures.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-red-700 mb-2 flex items-center gap-2">
                    <XCircle size={16} />
                    What You'll Lose
                    <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full">-{removedFeatures.length} features</span>
                  </h4>
                  <ul className="space-y-1.5">
                    {removedFeatures.map((feature, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-red-800 bg-red-50 p-2 rounded border border-red-100">
                        <XCircle size={14} className="flex-shrink-0 mt-0.5 text-red-600" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Downgrade Warning */}
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-amber-800 text-sm">
                  <strong>⚠️ Important:</strong> Downgrading takes effect at the end of your current billing period. 
                  You'll lose access to the features listed above.
                </p>
              </div>
            </>
          )}

          {/* Same Tier */}
          {isSameTier && (
            <div className="flex items-center gap-2 text-blue-700 text-sm p-3 bg-slate-50 rounded-lg border border-slate-100">
              <Minus size={16} />
              <span>Both plans are at the same tier with similar features.</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
